import { useEffect, useRef } from 'react';

/**
 * Hook condivisi per overlay premium (modali, wizard, command palette):
 *  - useScrollLock: blocca lo scroll del body mentre l'overlay è aperto,
 *    con ref-count a livello modulo (gestisce overlay annidati: es.
 *    NewProjectFlow che apre newQuote) e compensazione scrollbar.
 *  - useFocusTrap: salva il focus, autofoca il primo campo, cicla Tab/Shift+Tab
 *    dentro l'overlay, ripristina il focus all'elemento precedente al cleanup.
 */

// ─── Scroll lock con ref-count globale ───────────────────────────
let lockCount = 0;
let savedPaddingRight = '';
let savedOverflow = '';

function applyLock() {
  if (lockCount === 0) {
    const sw = window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    // Compensa la scomparsa della scrollbar per evitare "salto" del layout
    if (sw > 0) document.body.style.paddingRight = `${sw}px`;
  }
  lockCount++;
}
function releaseLock() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
}

export function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return;
    applyLock();
    return () => releaseLock();
  }, [active]);
}

// ─── Focus trap + return focus ──────────────────────────────────
const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getTabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * @param ref      container dell'overlay
 * @param active   se true il trap è attivo
 */
export function useFocusTrap(ref: React.RefObject<HTMLElement>, active = true) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !ref.current) return;
    const container = ref.current;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Autofocus: preferisci il primo CAMPO di input; fallback al primo tabbable
    // che NON sia marcato data-noautofocus (es. la X di chiusura).
    const focusFirst = () => {
      const fields = container.querySelectorAll<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
      );
      const firstField = Array.from(fields).find((el) => el.offsetParent !== null);
      if (firstField) { firstField.focus(); return; }
      const tabbables = getTabbables(container).filter((el) => !el.hasAttribute('data-noautofocus'));
      tabbables[0]?.focus();
    };
    const t = setTimeout(focusFirst, 50);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // Ricalcola a ogni Tab: i campi condizionali possono cambiare
      const tabbables = getTabbables(container);
      if (tabbables.length === 0) return;
      const first = tabbables[0]!;
      const last = tabbables[tabbables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement;
      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(t);
      container.removeEventListener('keydown', onKeyDown);
      // Return focus all'elemento che aveva il focus prima dell'apertura
      previouslyFocused.current?.focus?.();
    };
  }, [ref, active]);
}

/**
 * Combo: scroll-lock + focus-trap su un overlay. Crea e ritorna la ref da
 * appendere al container dell'overlay. Riduce il boilerplate nei 3 wizard.
 */
export function useOverlayA11y<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  useScrollLock(true);
  useFocusTrap(ref, true);
  return ref;
}
